/**
 * Eén afspraak als .ics-bestand ("Zet in je agenda"). Werkt in Apple Agenda, Google en Outlook.
 * Tijd in Europe/Amsterdam; de UID is het afspraak-id, zodat een tweede download dezelfde
 * afspraak bijwerkt in plaats van een dubbele te maken. Zodra de maildienst draait, stuurt de
 * server dezelfde uitnodiging mee in de bevestiging (zie docs/LARAVEL.md, Agenda).
 */
export type CalendarEvent = {
  id: string
  date: string // 2026-10-03
  time: string // 10:30
  durationMinutes: number
  title: string
  location: string
  description?: string
}

/** Europe/Amsterdam: zomertijd vanaf de laatste zondag van maart, wintertijd vanaf de laatste zondag van oktober. */
const AMSTERDAM = [
  'BEGIN:VTIMEZONE',
  'TZID:Europe/Amsterdam',
  'BEGIN:DAYLIGHT',
  'TZOFFSETFROM:+0100',
  'TZOFFSETTO:+0200',
  'TZNAME:CEST',
  'DTSTART:19700329T020000',
  'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU',
  'END:DAYLIGHT',
  'BEGIN:STANDARD',
  'TZOFFSETFROM:+0200',
  'TZOFFSETTO:+0100',
  'TZNAME:CET',
  'DTSTART:19701025T030000',
  'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU',
  'END:STANDARD',
  'END:VTIMEZONE',
]

const pad = (n: number) => String(n).padStart(2, '0')

const stamp = (date: string, minutes: number) => {
  const [y, m, d] = date.split('-').map(Number)
  const at = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, 0, minutes)
  return `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}T${pad(at.getHours())}${pad(at.getMinutes())}00`
}

/** Komma's, puntkomma's en regeleinden moeten ge-escaped worden in iCalendar-tekst. */
const text = (s: string) => s.replace(/\\/g, '\\\\').replace(/([,;])/g, '\\$1').replace(/\r?\n/g, '\\n')

export function calendarFile(e: CalendarEvent): string {
  const [h = 0, min = 0] = e.time.split(':').map(Number)
  const start = h * 60 + min
  const now = new Date()
  const utc = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}00Z`
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Beauty Bewust//Afspraak//NL',
    'CALSCALE:GREGORIAN',
    // De tijdzone hoort er volgens de standaard bij; Outlook negeert TZID zonder dit blok.
    ...AMSTERDAM,
    'BEGIN:VEVENT',
    `UID:${e.id}@beautybewust.nl`,
    `DTSTAMP:${utc}`,
    `DTSTART;TZID=Europe/Amsterdam:${stamp(e.date, start)}`,
    `DTEND;TZID=Europe/Amsterdam:${stamp(e.date, start + e.durationMinutes)}`,
    `SUMMARY:${text(e.title)}`,
    `LOCATION:${text(e.location)}`,
    ...(e.description ? [`DESCRIPTION:${text(e.description)}`] : []),
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'TRIGGER:-PT2H',
    `DESCRIPTION:${text(e.title)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')
}

/** Download starten; geeft false als de browser het blokkeert. */
export function downloadCalendarFile(e: CalendarEvent): boolean {
  try {
    const url = URL.createObjectURL(new Blob([calendarFile(e)], { type: 'text/calendar;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `afspraak-${e.date}.ics`
    document.body.appendChild(link)
    link.click()
    link.remove()
    // Tijdelijke link: meteen na de download weer vrijgeven.
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    return true
  } catch {
    return false
  }
}
